"use client";

import * as React from "react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { X } from "lucide-react";
interface CaseStudy {
  id: number;
  title: string;
  category: string;
  image: string;
  description: string;
  challenge: string;
  solution: string;
  results: string;
}
const caseStudies: CaseStudy[] = [{
  id: 1,
  title: "Luxury Property Portfolio - Ikoyi",
  category: "Real Estate",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/case_study_ikoyi_luxury_property.png",
  description: "Complete acquisition and marketing strategy for high-end residential development in Ikoyi, Lagos.",
  challenge: "Client needed to acquire premium property and establish digital presence in competitive luxury market.",
  solution: "Implemented data-driven property sourcing combined with targeted SEO campaign and Google Business Profile optimization.",
  results: "Sold 70% of units within 4 months. 340% increase in qualified leads through organic search."
}, {
  id: 2,
  title: "Commercial Real Estate - Abuja",
  category: "Real Estate",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/case_study_abuja_commercial_space.png",
  description: "Investment analysis and digital visibility strategy for commercial property development in Abuja Central Business District.",
  challenge: "Developer required comprehensive market intelligence and lead generation for commercial spaces.",
  solution: "Deployed Nethill Method combining real estate analytics with SEO-optimized content marketing and targeted PPC campaigns.",
  results: "Generated 180 qualified business inquiries in 6 months. Achieved top 3 Google rankings for 12 commercial real estate keywords."
}, {
  id: 3,
  title: "Residential Development - Port Harcourt",
  category: "Real Estate",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/case_study_port_harcourt_residential.png",
  description: "End-to-end property sourcing and digital marketing for mid-range residential estate in Port Harcourt.",
  challenge: "Client needed to identify prime land parcels and build buyer interest before construction completion.",
  solution: "Leveraged local market intelligence for acquisition, launched interactive website with virtual tours and aggressive social media strategy.",
  results: "Secured off-plan sales for 55% of units. Website traffic increased 420% with 2.3% conversion rate."
}, {
  id: 4,
  title: "Investment Portfolio Optimization",
  category: "Investment Analysis",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/case_study_portfolio_optimization.png",
  description: "Strategic portfolio management and digital branding for diaspora investor with multi-property holdings across Nigeria.",
  challenge: "Investor needed centralized portfolio oversight and enhanced online reputation to attract joint venture partners.",
  solution: "Created custom portfolio dashboard, implemented comprehensive SEO audit, and built authority through thought leadership content.",
  results: "Portfolio ROI improved 28%. Client featured in 5 major real estate publications. Secured 3 joint venture partnerships."
}, {
  id: 5,
  title: "Real Estate Agency Digital Transformation",
  category: "Digital Marketing",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/case_study_agency_digital_transformation.png",
  description: "Complete digital overhaul for established Lagos-based real estate agency seeking online visibility.",
  challenge: "Agency had zero digital presence despite 15 years in business. Needed immediate online credibility and lead flow.",
  solution: "Built SEO-optimized website, claimed and optimized Google Business Profile, launched content marketing strategy with local SEO focus.",
  results: "Agency now ranks page 1 for 18 Lagos real estate keywords. 210% increase in inbound inquiries. 4.8-star Google rating with 87 reviews."
}, {
  id: 6,
  title: "Land Banking Strategy - Multiple States",
  category: "Investment Analysis",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/case_study_land_banking_strategy.png",
  description: "Strategic land acquisition program across Lagos, Ogun, and Oyo states with market intelligence support.",
  challenge: "High-net-worth client required expert guidance to identify undervalued land parcels with appreciation potential.",
  solution: "Applied proprietary market analysis framework combining demographic trends, infrastructure development plans, and zoning regulations.",
  results: "Acquired 12 prime land parcels. Average appreciation 45% within 18 months. Client portfolio value increased by N320 million."
}];
export default function CaseStudyGallery() {
  const [selectedStudy, setSelectedStudy] = useState<CaseStudy | null>(null);
  return <section className="bg-background py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Case Studies
          </h2>
          <p className="text-muted-foreground text-lg">
            Real results from real clients. Explore how Nethill Limited transforms real estate acquisitions and digital visibility into measurable success.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {caseStudies.map((study, i) => <Card key={study.id} data-index={i} className="group cursor-pointer overflow-hidden border border-border shadow-soft transition-all duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:shadow-medium" onClick={() => setSelectedStudy(study)}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={study.image} alt={study.title} className="size-full object-cover transition-transform duration-[250ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.02]" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/90">
                    {study.category}
                  </p>
                  <h3 className="mt-1 text-lg font-bold leading-tight text-white">
                    {study.title}
                  </h3>
                </div>
              </div>
            </Card>)}
        </div>
      </div>

      <Dialog open={!!selectedStudy} onOpenChange={() => setSelectedStudy(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          {selectedStudy && <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedStudy.title}</DialogTitle>
                <DialogDescription className="text-base">
                  {selectedStudy.category}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                <img src={selectedStudy.image} alt={selectedStudy.title} className="w-full rounded-lg object-cover" />
                <div>
                  <p className="text-muted-foreground">{selectedStudy.description}</p>
                </div>
                <div>
                  <h4 className="mb-2 text-lg font-semibold text-foreground">Challenge</h4>
                  <p className="text-muted-foreground">{selectedStudy.challenge}</p>
                </div>
                <div>
                  <h4 className="mb-2 text-lg font-semibold text-foreground">Solution</h4>
                  <p className="text-muted-foreground">{selectedStudy.solution}</p>
                </div>
                <div className="rounded-lg bg-primary-light p-4">
                  <h4 className="mb-2 text-lg font-semibold text-primary">Results</h4>
                  <p className="text-foreground">{selectedStudy.results}</p>
                </div>
              </div>
            </>}
        </DialogContent>
      </Dialog>
    </section>;
}